/**
 * GRNScreen — Goods Receipt Note (3 modes)
 * Screen IDs (from USER_SCREENS production DB):
 *   4.8.14 = GRN Against P.O (New UI)
 *   4.8.15 = GRN Against P.I (New UI)
 *   4.8.16 = GRN Return (New UI)
 *   4.8.17 = Purchase Invoice Return (New UI) → handled by PurchaseInvoiceV2Screen
 *
 * UX flow: Landing → Transactions list → "New Entry" → Entry form
 * Features: F2/F3 New Item, F10 Size Config, F8 Save, Esc, inline grid,
 *   row inspector (Cls/Pricing/Image/Info), Bill Details, Tax Breakup, Amount Details
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Download, Columns2, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import ERPAdvancedProcGrid from '../../../components/procurement/ERPAdvancedProcGrid';
import ProcItemGrid from '../../../components/procurement/ProcItemGrid';
import NewItemModal from '../../../components/procurement/NewItemModal';
import SizeConfigModal from '../../../components/procurement/SizeConfigModal';
import { ItemHistoryModal, SupplierHistoryModal } from '../../../components/procurement/ProcRowModals';
import ImageManagerModal from '../../../components/procurement/ImageManagerModal';
import UnitIdentifierModal from '../../../components/procurement/UnitIdentifierModal';
import { loadImages } from '../../../lib/imageStorage';
import GetInfoPopup from '../../../components/GetInfoPopup';
import '../purchase-invoice/PurchaseInvoiceV2.css';
import { API_BASE, getToken } from '../../../lib/api';

// ─── Mode config ───────────────────────────────────────────────────────────
// Correct IDs from USER_SCREENS production DB:
//   4.8.14 = GRN Against P.O (New UI)
//   4.8.15 = GRN Against P.I (New UI)
//   4.8.16 = GRN Return (New UI)
const GRN_AGST_PO_IDS = new Set(['4.8.14', 'GRN_AGAINST_PO_SCREEN']);
const GRN_AGST_PI_IDS = new Set(['4.8.15', 'GRN_AGAINST_PI_SCREEN']);
const GRN_RETURN_IDS  = new Set(['4.8.16', 'GRN_RETURN_SCREEN']);

function getGRNConfig(sid) {
  if (GRN_RETURN_IDS.has(sid)) return {
    sid, isReturn: true,
    title: 'GRN Return',
    fullTitle: 'GRN Return (Goods Receipt Return)',
    crumb: 'Procurement › Goods Receipts & Barcode › GRN Return',
    tab1: 'General', t1k: 'F3', tab2: 'Against P.O', t2k: 'F4',
    docLabel: 'GRN Return No', prevValue: 'GRR101N0004',
    docInfoTitle: 'Return Information',
    grcInfoTitle: 'GRC Information',
    gFields: [
      { id:'retDate',     label:'Return Date',       type:'date' },
      { id:'retRef',      label:'Return Ref',         type:'text' },
      { id:'againstGrnNo',label:'Against GRN No',     type:'lkp', si:{screenId:'4.8.1',controlId:'7'}, lt:'Select GRN' },
      { id:'grnDate',     label:'GRN Date',           type:'date' },
      { id:'payMode',     label:'Pay Mode',           type:'sel', opts:['Credit','Cash','NEFT','RTGS','UPI'] },
      { id:'supplier',    label:'Supplier',           type:'lkp', si:{screenId:'3.2',controlId:'5'}, lt:'Select Supplier' },
      { id:'purchAc',     label:'Purchase A/C',       type:'lkp', si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account' },
      { id:'reason',      label:'Reason For Return',  type:'text' },
      { id:'remarks',     label:'Remarks',            type:'text' },
    ],
    poFields: [
      { id:'apoDate',  label:'A.P.O Date',    type:'date' },
      { id:'mrRef',    label:'M.R/L.R Ref',   type:'text' },
      { id:'poRef',    label:'P.O',           type:'lkp', si:{screenId:'4.4.2',controlId:'1'}, lt:'Select PO' },
      { id:'invDate',  label:'Invoice Date',  type:'date' },
      { id:'payMode',  label:'Pay Mode',      type:'sel', opts:['Credit','Cash','NEFT','RTGS','UPI'] },
      { id:'supplier', label:'Supplier',      type:'lkp', si:{screenId:'3.2',controlId:'5'}, lt:'Select Supplier' },
      { id:'purchAc',  label:'Purchase A/C',  type:'lkp', si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account' },
      { id:'remarks',  label:'Remarks',       type:'text' },
    ],
  };

  if (GRN_AGST_PO_IDS.has(sid)) return {
    sid, isReturn: false,
    title: 'GRN Against P.O',
    fullTitle: 'GRN — Against Purchase Order',
    crumb: 'Procurement › Goods Receipts & Barcode › GRN Against P.O',
    tab1: 'General', t1k: 'F3', tab2: 'Against P.O', t2k: 'F4',
    docLabel: 'GRN No', prevValue: 'GRN101N0005',
    docInfoTitle: 'Document Information',
    grcInfoTitle: 'GRC Information',
    gFields: [
      { id:'mrDate',     label:'M.R Date',      type:'date' },
      { id:'mrRef',      label:'M.R Ref',        type:'text' },
      { id:'mrTemplate', label:'M.R Template',   type:'text' },
      { id:'piDate',     label:'P.Invoice Date', type:'date' },
      { id:'payMode',    label:'Pay Mode',       type:'sel', opts:['Credit','Cash','NEFT','RTGS','UPI'] },
      { id:'supplier',   label:'Supplier',       type:'lkp', si:{screenId:'3.2',controlId:'5'}, lt:'Select Supplier' },
      { id:'purchAc',    label:'Purchase A/C',   type:'lkp', si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account' },
      { id:'remarks',    label:'Remarks',        type:'text' },
    ],
    poFields: [
      { id:'poDate',      label:'P.O Date',        type:'date' },
      { id:'lrRef',       label:'L.R Ref',          type:'text' },
      { id:'againstPoNo', label:'Against P.O No',   type:'lkp', si:{screenId:'4.4.2',controlId:'1'}, lt:'Select PO' },
      { id:'invDate',     label:'Invoice Date',     type:'date' },
      { id:'payMode',     label:'Pay Mode',         type:'sel', opts:['Credit','Cash','NEFT','RTGS','UPI'] },
      { id:'supplier',    label:'Supplier',         type:'lkp', si:{screenId:'3.2',controlId:'5'}, lt:'Select Supplier' },
      { id:'purchAc',     label:'Purchase A/C',     type:'lkp', si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account' },
      { id:'remarks',     label:'Remarks',          type:'text' },
    ],
  };

  // Default: GRN Against PI (4.8.15)
  return {
    sid, isReturn: false,
    title: 'GRN Against P.I',
    fullTitle: 'GRN — Against Proforma Invoice (P.I)',
    crumb: 'Procurement › Goods Receipts & Barcode › GRN Against P.I',
    tab1: 'General', t1k: 'F3', tab2: 'Against P.O', t2k: 'F4',
    docLabel: 'GRN No', prevValue: 'GRN101N0006',
    docInfoTitle: 'Document Information',
    grcInfoTitle: 'GRC Information',
    gFields: [
      { id:'mrDate',     label:'M.R Date',      type:'date' },
      { id:'mrRef',      label:'M.R Ref',        type:'text' },
      { id:'mrTemplate', label:'M.R Template',   type:'text' },
      { id:'piDate',     label:'P.Invoice Date', type:'date' },
      { id:'payMode',    label:'Pay Mode',       type:'sel', opts:['Credit','Cash','NEFT','RTGS','UPI'] },
      { id:'supplier',   label:'Supplier',       type:'lkp', si:{screenId:'3.2',controlId:'5'}, lt:'Select Supplier' },
      { id:'purchAc',    label:'Purchase A/C',   type:'lkp', si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account' },
      { id:'remarks',    label:'Remarks',        type:'text' },
    ],
    poFields: [
      { id:'piDate2',     label:'P.I Date',       type:'date' },
      { id:'lrRef',       label:'L.R Ref',         type:'text' },
      { id:'againstPiNo', label:'Against P.I No',  type:'lkp', si:{screenId:'4.10.3',controlId:'1'}, lt:'Select P.I' },
      { id:'invDate',     label:'Invoice Date',    type:'date' },
      { id:'payMode',     label:'Pay Mode',        type:'sel', opts:['Credit','Cash','NEFT','RTGS','UPI'] },
      { id:'supplier',    label:'Supplier',        type:'lkp', si:{screenId:'3.2',controlId:'5'}, lt:'Select Supplier' },
      { id:'purchAc',     label:'Purchase A/C',    type:'lkp', si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account' },
      { id:'remarks',     label:'Remarks',         type:'text' },
    ],
  };
}

// ─── Grid columns (identical to PurchaseInvoiceV2) ─────────────────────────
const CORE_COLS = [
  {id:'code',   label:'ItemCode',    w:90,  num:false},
  {id:'hsn',    label:'HSN',         w:72,  num:false},
  {id:'name',   label:'Item Name',   w:160, num:false, ro:true},
  {id:'artCode',label:'Article Code',w:80,  num:false},
  {id:'oemCode',label:'OEM Code',    w:68,  num:false},
  {id:'uom',    label:'UOM',         w:50,  num:false},
  {id:'qty',    label:'Qty',         w:55,  num:true,  req:true},
  {id:'rate',   label:'Rate',        w:68,  num:true,  req:true},
  {id:'price',  label:'Price',       w:75,  num:true,  ro:true},
  {id:'mrp',    label:'MRP',         w:68,  num:true},
  {id:'wsp',    label:'WSP Rate',    w:68,  num:true},
  {id:'disc',   label:'Disc',        w:55,  num:true},
  {id:'pn',     label:'P/N',         w:38,  num:false},
  {id:'cgst',   label:'CGST%',       w:52,  num:true},
  {id:'sgst',   label:'SGST%',       w:52,  num:true},
  {id:'amount', label:'Amount',      w:88,  num:true,  ro:true},
];
const CORE_COL_IDS = new Set(CORE_COLS.map(c=>c.id));
const EXTRA_COLS = [
  {id:'division', label:'Division'}, {id:'dept',      label:'Dept'},
  {id:'category', label:'Category'}, {id:'subcatg',   label:'Sub Catg'},
  {id:'brand',    label:'Brand'},    {id:'style',     label:'Style'},
  {id:'size',     label:'Size'},     {id:'color',     label:'Color'},
  {id:'design',   label:'Design'},   {id:'smToken',   label:'SM Token'},
  {id:'commission',label:'Commission %'},{id:'scheme', label:'Scheme'},
  {id:'batchNo',  label:'Batch No'}, {id:'mfgDate',   label:'Mfg Date'},
  {id:'expiryDate',label:'Expiry Date'},{id:'remarks', label:'Remarks'},
].filter(c=>!CORE_COL_IDS.has(c.id));

const BLANK = () => ({
  _id: Date.now()+Math.random(), gstType:'excl',
  code:'', hsn:'', name:'', artCode:'', oemCode:'', uom:'',
  qty:'', rate:'', price:'', mrp:'', wsp:'', disc:'0', pn:'P',
  cgst:'2.50', sgst:'2.50', amount:'',
  division:'', dept:'', category:'', subcatg:'', brand:'', style:'',
  size:'', color:'', design:'', cref:'',
  smToken:'', commission:'0.00', scheme:'', batchNo:'', mfgDate:'', expiryDate:'', remarks:'',
});

const CLS_LKP = {
  division:'5.5.8/1', dept:'5.5.11/1', category:'5.5.2/1',
  subcatg:'5.5.12/1', brand:'5.5.3/1', style:'5.5.10/1',
  size:'5.5.9/1',     color:'5.5.4/1', design:'5.5.5/1',
};
function parseSI(str) { const [screenId,controlId]=str.split('/'); return {screenId,controlId}; }

const AUTOFILL = {
  Division:['Textiles','Electronics','Footwear','Cosmetics','Jewellery','Pharma','Grocery','Home Decor'],
  Dept:['Mens','Womens','Kids','Unisex','Accessories'],
  Category:['Shirts','Trousers','Sarees','T-Shirts','Jackets','Footwear','Bags','Watches'],
  Subcatg:['P Sarees','Casual','Formal','Sports'],
  Brand:['Nike','Adidas','Puma','H&M','Zara'],
  Style:['Regular','Slim Fit','Loose Fit'],
  Size:['XS','S','M','L','XL','XXL','28','30','32','34','36'],
  Color:['Red','Blue','Green','Black','White','Grey'],
  Design:['Solid','Striped','Printed','Checks'],
};

function fmt2(v) { return (parseFloat(v)||0).toFixed(2); }
function fmtIn(v){ return (parseFloat(v)||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2}); }
function nowStr(){ return new Date().toLocaleTimeString('en-IN',{hour12:false}); }
function todayStr(){ return new Date().toISOString().slice(0,10); }
function hdrs(){ const t=getToken(); return {'Content-Type':'application/json',Authorization:t?`Bearer ${t}`:''}; }

function calcRow(r) {
  const qty=parseFloat(r.qty)||0, rate=parseFloat(r.rate)||0, disc=parseFloat(r.disc)||0;
  const cgst=parseFloat(r.cgst)||0, sgst=parseFloat(r.sgst)||0;
  const price=qty*rate, discA=price*disc/100, base=price-discA;
  const gstA=base*(cgst+sgst)/100;
  return {...r, price:price.toFixed(2), amount:(r.gstType==='incl'?price:base+gstA).toFixed(2)};
}

// ─── AutoComplete ──────────────────────────────────────────────────────────
function AutoInput({ id, value, dataKey, onChange }) {
  const [open,setOpen]=useState(false);
  const [q,setQ]=useState(value||'');
  const ref=useRef(null);
  const list=(AUTOFILL[dataKey]||[]).filter(x=>!q||x.toLowerCase().includes(q.toLowerCase())).slice(0,10);
  useEffect(()=>{setQ(value||'');},[value]);
  useEffect(()=>{
    if(!open) return;
    const h=(e)=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener('mousedown',h); return ()=>document.removeEventListener('mousedown',h);
  },[open]);
  return (
    <div className="pi2-autoinput" ref={ref}>
      <input id={id} value={q} autoComplete="off"
        onChange={e=>{setQ(e.target.value);onChange(e.target.value);setOpen(true);}}
        onFocus={()=>setOpen(true)} />
      <button type="button" className="pi2-lkpbtn" onClick={()=>setOpen(o=>!o)}>🔍</button>
      {open&&list.length>0&&(
        <div className="pi2-auto-dd">
          {list.map(x=><button key={x} type="button" onClick={()=>{setQ(x);onChange(x);setOpen(false);}}>{x}</button>)}
        </div>
      )}
    </div>
  );
}


// ─── GRN Transactions List — uses ERPAdvancedProcGrid ──────────────────────
function GRNList({ screenId, onNewEntry, onEditEntry, onBack }) {
  const cfg = getGRNConfig(screenId);
  const cols = cfg.isReturn
    ? [
        {field:'retNo',      label:'RETURN NO'},
        {field:'retDate',    label:'DATE'},
        {field:'supplier',   label:'SUPPLIER NAME'},
        {field:'againstGrn', label:'AGAINST GRN'},
        {field:'items',      label:'ITEMS'},
        {field:'netAmt',     label:'NET AMOUNT'},
        {field:'status',     label:'STATUS'},
      ]
    : [
        {field:'grnNo',    label:'GRN NO'},
        {field:'grnDate',  label:'DATE'},
        {field:'supplier', label:'SUPPLIER NAME'},
        ...(cfg.title.includes('P.O')?[{field:'poRef',label:'AGAINST P.O'}]:[]),
        ...(cfg.title.includes('P.I')?[{field:'piRef',label:'AGAINST P.I'}]:[]),
        {field:'items',    label:'ITEMS'},
        {field:'netAmt',   label:'NET AMOUNT'},
        {field:'status',   label:'STATUS'},
      ];
  return (
    <ERPAdvancedProcGrid
      title={cfg.fullTitle}
      screenId={cfg.sid}
      columns={cols}
      records={[]}
      itemCols={[{field:'barcode',label:'BARCODE'},{field:'itemName',label:'ITEM NAME'},{field:'qty',label:'QTY'}]}
      onAddNew={onNewEntry}
      onBack={onBack}
      onEdit={onEditEntry}
      onView={onEditEntry}
      onDelete={r=>alert(`Delete ${r.grnNo||r.retNo}?`)}
      onPrint={r=>alert(`Print ${r.grnNo||r.retNo}`)}
    />
  );
}

// ─── GRN Entry Form ────────────────────────────────────────────────────────
function GRNForm({ screenId, editId, onBack }) {
  const cfg = getGRNConfig(screenId);
  const [mode,        setMode]       = useState('general');
  const [hdr,         setHdr]        = useState({});
  const [suppInfo,    setSuppInfo]   = useState(null);
  const [rows,        setRows]       = useState([
    {...BLANK(), _id:1, code:'1127000206', hsn:'62031990', name:'Printed Saree', artCode:'A0000206', oemCode:'2000', uom:'Qty', qty:'1', rate:'520.00', price:'520.00', mrp:'999.00', wsp:'0.00', disc:'0', pn:'P', cgst:'2.50', sgst:'2.50', amount:'520.00', division:'Textiles', dept:'Mens', category:'Shirts', subcatg:'P Sarees', brand:'B', style:'MyAccounts', size:'-', color:'C', design:'D'},
    {...BLANK(), _id:2, code:'1127000207', hsn:'62031990', name:'Cotton Saree',  artCode:'A0000207', oemCode:'2001', uom:'Qty', qty:'2', rate:'520.00', price:'1040.00', mrp:'999.00', wsp:'0.00', disc:'60', pn:'P', cgst:'2.50', sgst:'2.50', amount:'1040.00', division:'Textiles', dept:'Mens', category:'Shirts', subcatg:'P Sarees', brand:'B', style:'MyAccounts', size:'-', color:'C', design:'D', gstType:'excl'},
  ]);
  const [selIdx,      setSelIdx]     = useState(null);
  const [insTab,      setInsTab]     = useState('cls');
  const [insVals,     setInsVals]    = useState({});
  const [bill,        setBill]       = useState({discPct:'0.00',freight:'0.00',roundOff:'0.00',freightGst:false});
  const [popup,       setPopup]      = useState(null);
  const [showNewItem, setShowNewItem]= useState(false);
  const [showSizeCfg, setShowSizeCfg]= useState(false);
  const [editRow,     setEditRow]    = useState(null);
  const [histModal,   setHistModal]  = useState(null); // {type:'item'|'supplier', row}
  const [imgModal,    setImgModal]   = useState(null); // { row, idx }
  const [imeiModal2,  setImeiModal2] = useState(null); // row for UnitIdentifierModal
  const [importOpen,  setImportOpen] = useState(false);
  const [rdCollapsed, setRdCollapsed]= useState(false);
  const [toast,       setToast]      = useState('');
  const [toastVis,    setToastVis]   = useState(false);
  const [clock,       setClock]      = useState(nowStr());

  const toastRef  = useRef(null);

  const curFields  = mode==='general' ? cfg.gFields : cfg.poFields;
  const selRow     = selIdx!==null ? rows[selIdx] : null;

  useEffect(()=>{ const t=setInterval(()=>setClock(nowStr()),1000); return ()=>clearInterval(t); },[]);

  // Keyboard shortcuts
  useEffect(()=>{
    const h=(e)=>{
      if(e.key==='F2'){ e.preventDefault(); setEditRow(null); setShowNewItem(true); }
      if(e.key==='F3'){ e.preventDefault(); setEditRow(selRow||null); setShowNewItem(true); }
      if(e.key==='F10'){ e.preventDefault(); setShowSizeCfg(true); }
      if(e.key==='F8') { e.preventDefault(); handleSave(); }
      // Ctrl+F2 = Image Manager for selected row
      if(e.ctrlKey&&e.key==='F2'){ e.preventDefault(); if(selRow) setImgModal({row:selRow,idx:selIdx}); else showToastMsg('Select a row first (Ctrl+F2 = Image Manager)'); }
      // Ctrl+F3 = IMEI/Serial for selected row
      if(e.ctrlKey&&e.key==='F3'){ e.preventDefault(); if(selRow) setImeiModal2(selRow); else showToastMsg('Select a row first (Ctrl+F3 = IMEI/Serial)'); }
      if(e.key==='Escape'){
        e.preventDefault();
        if(showNewItem){ setShowNewItem(false); return; }
        if(showSizeCfg){ setShowSizeCfg(false); return; }
        if(popup){ setPopup(null); return; }
        if(selIdx!==null){ setSelIdx(null); setInsVals({}); return; }
        onBack();
      }
    };
    window.addEventListener('keydown',h); return ()=>window.removeEventListener('keydown',h);
  });

  // Totals
  const totQty    = rows.reduce((s,r)=>s+(parseFloat(r.qty)||0),0);
  const totAmount = rows.reduce((s,r)=>s+(parseFloat(r.amount)||0),0);
  const totDisc   = rows.reduce((s,r)=>s+(parseFloat(r.qty)||0)*(parseFloat(r.rate)||0)*(parseFloat(r.disc)||0)/100,0);
  const totGST    = rows.reduce((s,r)=>{
    const base=(parseFloat(r.qty)||0)*(parseFloat(r.rate)||0)*(1-(parseFloat(r.disc)||0)/100);
    return s+base*((parseFloat(r.cgst)||0)+(parseFloat(r.sgst)||0))/100;
  },0);
  const freightGST = bill.freightGst?(parseFloat(bill.freight)||0)*0.18:0;
  const cgstAmt=totGST/2, sgstAmt=totGST/2;
  const grossAmt=totAmount;
  const discAmt=grossAmt*(parseFloat(bill.discPct)||0)/100;
  const netAmt=grossAmt-discAmt+(parseFloat(bill.freight)||0)+freightGST+(parseFloat(bill.roundOff)||0);

  const showToastMsg=useCallback((msg)=>{
    setToast(msg); setToastVis(true);
    clearTimeout(toastRef.current);
    toastRef.current=setTimeout(()=>setToastVis(false),2200);
  },[]);

  function updRow(idx,field,value){
    setRows(prev=>{const next=[...prev];next[idx]=calcRow({...next[idx],[field]:value});return next;});
    if(idx===selIdx) setInsVals(v=>({...v,[field]:value}));
  }
  function addEmptyRow(){ setRows(p=>[...p,BLANK()]); showToastMsg('New row added'); }
  function deleteRow(i){
    if(rows.length===1){setRows([BLANK()]);setSelIdx(null);return;}
    setRows(p=>p.filter((_,j)=>j!==i));
    if(selIdx===i) setSelIdx(null);
    else if(selIdx>i) setSelIdx(selIdx-1);
  }
  function selectRow(i){ setSelIdx(i); setInsVals({...rows[i]}); setInsTab('cls'); }
  function handleInsChange(k,v){ setInsVals(p=>({...p,[k]:v})); if(selIdx!==null) updRow(selIdx,k,v); }
  function setHdrField(id,display,raw){ setHdr(p=>({...p,[id]:display,[`${id}_raw`]:raw??display})); if(id==='supplier'&&raw) fetchSupplier(raw); }
  async function fetchSupplier(id){
    try{ const r=await fetch(`${API_BASE}/api/supplier-master/${id}`,{headers:hdrs()}); const d=await r.json(); if(d.success) setSuppInfo(d.data); }catch(_){}
  }
  function openLookup(si,lt,cb){ if(!si){showToastMsg('No lookup configured');return;} setPopup({si,title:lt,cb}); }
  function handleLkpSelect(row,cols){
    const dc=cols.find(c=>c.displayMember)||cols[0];
    const vc=cols.find(c=>c.valueMember)||cols[0];
    const dv=row[dc?.field||Object.keys(row)[0]]||'';
    const vv=row[vc?.field||Object.keys(row)[0]]||'';
    if(popup?.cb) popup.cb(dv,vv,row);
    setPopup(null);
  }
  function handleSave(){
    if(!hdr.supplier){showToastMsg('Supplier is required');return;}
    const items=rows.filter(r=>r.code||r.qty);
    if(!items.length){showToastMsg('Add at least one item');return;}
    showToastMsg(`${cfg.title} saved! Net ₹${fmtIn(netAmt)}`);
  }
  function handleClear(){
    setHdr({});setSuppInfo(null);
    setRows([BLANK()]);setSelIdx(null);setInsVals({});
    setBill({discPct:'0.00',freight:'0.00',roundOff:'0.00',freightGst:false});
    showToastMsg('Screen cleared');
  }


  // ── Header field renderer ──
  function renderHdrField(f){
    const val=hdr[f.id]||'';
    if(f.type==='date') return (
      <div className="pi2-field" key={f.id}><label>{f.label}</label>
        <input type="date" value={val||todayStr()} onChange={e=>setHdrField(f.id,e.target.value)}/></div>
    );
    if(f.type==='sel') return (
      <div className="pi2-field" key={f.id}><label>{f.label}</label>
        <select value={val} onChange={e=>setHdrField(f.id,e.target.value)}>
          {(f.opts||[]).map(o=><option key={o}>{o}</option>)}
        </select></div>
    );
    if(f.type==='lkp') return (
      <div className="pi2-field" key={f.id}><label>{f.label}</label>
        <div className="pi2-autoinput">
          <input value={val} onChange={e=>setHdrField(f.id,e.target.value)} data-testid={`grn-hdr-${f.id}`}/>
          <button type="button" className="pi2-lkpbtn"
            onClick={()=>openLookup(f.si,f.lt,(d,r)=>setHdrField(f.id,d,r))}>🔍</button>
        </div></div>
    );
    return (
      <div className="pi2-field" key={f.id}><label>{f.label}</label>
        <input value={val} onChange={e=>setHdrField(f.id,e.target.value)}/></div>
    );
  }

  function rowClass(r,i){
    if(i===selIdx) return 'selected';
    let cls=r.gstType==='incl'?'pi2-gst-incl':'pi2-gst-excl';
    if(parseFloat(r.disc)>0) cls+=' pi2-has-disc';
    return cls;
  }

  const clsFields=[
    {k:'division',lbl:'Division'},{k:'dept',lbl:'Department'},{k:'category',lbl:'Category'},
    {k:'subcatg',lbl:'Sub Category'},{k:'brand',lbl:'Brand'},{k:'style',lbl:'Style'},
    {k:'size',lbl:'Size'},{k:'color',lbl:'Color'},{k:'design',lbl:'Design'},{k:'cref',lbl:'C.REF'},
  ];

  return (
    <div className="pi2-app" data-testid={`grn-form-${screenId}`}
      data-dev-component="GRNForm" data-dev-file="GRNScreen.jsx">

      {/* Title bar */}
      <div className="pi2-titlebar">
        <div>
          <div className="pi2-crumb">
            <span style={{cursor:'pointer',opacity:.7}} onClick={onBack}>← {cfg.title}</span>
            &nbsp;› New Entry
          </div>
          <div className="pi2-heading-row">
            <h1 className="pi2-h1">{cfg.fullTitle}</h1>
            <div className="pi2-modetabs">
              <button className={mode==='general'?'active':''} onClick={()=>setMode('general')} data-testid="grn-tab-general">
                {cfg.tab1} <span className="pi2-fkey">{cfg.t1k}</span>
              </button>
              <button className={mode==='po'?'active':''} onClick={()=>setMode('po')} data-testid="grn-tab-po">
                {cfg.tab2} <span className="pi2-fkey">{cfg.t2k}</span>
              </button>
            </div>
            <div className="pi2-docbadges">
              <div className="pi2-docbadge hi"><span className="dl">{cfg.docLabel}</span><span className="dv">—</span></div>
              <div className="pi2-docbadge"><span className="dl">Previous Trans. No</span><span className="dv">{cfg.prevValue}</span></div>
            </div>
          </div>
        </div>
        <div className="pi2-fkeys">
          <span style={{cursor:'pointer'}} onClick={()=>{setEditRow(null);setShowNewItem(true);}}><b>F2</b> New Item</span>
          <span style={{cursor:'pointer'}} onClick={()=>{setEditRow(selRow||null);setShowNewItem(true);}}><b>F3</b> {selRow?'Edit Row':'New Item'}</span>
          <span style={{cursor:'pointer'}} onClick={()=>setShowSizeCfg(true)}><b>F10</b> Size Config</span>
          <span style={{cursor:'pointer'}} onClick={()=>{if(selRow)setImgModal({row:selRow,idx:selIdx});}}><b>Ctrl+F2</b> Image</span>
          <span style={{cursor:'pointer'}} onClick={()=>{if(selRow)setImeiModal2(selRow);}}><b>Ctrl+F3</b> IMEI/Serial</span>
          <span><b>F8</b> Save</span>
          <span><b>Esc</b> Back</span>
        </div>
      </div>

      {/* Body */}
      <div className="pi2-body">
        {/* Left column */}
        <div className="pi2-leftcol">
          {/* Header zone */}
          {/* Header zone: Doc Info (left) | Supplier Info (right) */}
          <div className="pi2-hdrzone">
            {/* Left: Document Information card (curFields switches with tab) */}
            <div className="pi2-card">
              <div className="pi2-cardhead"><span className="pi2-dot"/>{cfg.docInfoTitle}
                <button type="button" className="pi2-addfld"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-fieldgrid" data-testid="grn-doc-info-form">
                {curFields.map(f=>renderHdrField(f))}
              </div>
            </div>

            {/* Right: Supplier Information */}
            <div className="pi2-card">
              <div className="pi2-cardhead"><span className="pi2-dot"/>Supplier Information
                <button type="button" className="pi2-addfld"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-suppbox">
                <div className="pi2-supprow"><span className="k">GSTIN</span><span className="v">{suppInfo?.gstin||'—'}</span></div>
                <div className="pi2-supprow"><span className="k">St Code</span><span className="v">{suppInfo?.stateCode||'—'}</span></div>
                <div className="pi2-supprow"><span className="k">O/S</span><span className="v neg">{suppInfo?.outstanding||'—'}</span></div>
                <div className="pi2-supprow"><span className="k">Inv Amt</span><span className="v">{fmtIn(netAmt)}</span></div>
                <div className="pi2-supprow"><span className="k">Bal Amt</span><span className="v">0.00</span></div>
                <div className="pi2-supp-total"><span>Total</span><span className="v">{suppInfo?.outstanding||'—'}</span></div>
              </div>
            </div>
          </div>

          {/* Item grid */}
          <ProcItemGrid
            rows={rows}
            selIdx={selIdx}
            onRowChange={(idx, field, value) => updRow(idx, field, value)}
            onRowSelect={selectRow}
            onRowDelete={deleteRow}
            onRowEdit={idx => { setEditRow(rows[idx]); setShowNewItem(true); }}
            onRowVoid={idx => { showToastMsg(`Row ${idx+1} voided`); }}
            onRowDuplicate={idx => { setRows(p=>[...p,{...p[idx],_id:Date.now()+Math.random()}]); showToastMsg('Row duplicated'); }}
            onAddEmptyRow={addEmptyRow}
            // Context menu modals
            onRowItemHistory={idx => setHistModal({type:'item', row:rows[idx]})}
            onRowSupplierHistory={idx => setHistModal({type:'supplier', row:rows[idx]})}
            onRowSplitBarcode={idx => setImeiModal2(rows[idx])}
            totQty={totQty}
            totAmount={totAmount}
            totDisc={totDisc}
            totGST={totGST}
            roundOff={bill.roundOff}
          />

          {/* Row detail bar */}
          <div className={`pi2-rowdetail${rdCollapsed?' collapsed':''}`} data-testid="grn-row-detail-bar">
            <div className="pi2-rowdetail-head">
              <span className="pi2-dot"/>Selected Row — Additional Details
              <span className={`pi2-rowdetail-tag${selRow?' active':''}`}>{selRow?selRow.code||'Row selected':'No row selected'}</span>
              <button type="button" className="pi2-addfld" style={{marginLeft:8}}><span>+</span>Add Field</button>
              <button type="button" className="pi2-collapse-btn" onClick={()=>setRdCollapsed(v=>!v)}>
                {rdCollapsed?'▸ Minimize':'▾ Minimize'}
              </button>
            </div>
            <div className="pi2-rowdetail-fields">
              {[{k:'smToken',lbl:'SM Token'},{k:'commission',lbl:'Commission %'},{k:'scheme',lbl:'Scheme'},
                {k:'batchNo',lbl:'Batch No'},{k:'mfgDate',lbl:'Mfg Date'},{k:'expiryDate',lbl:'Expiry Date'},
                {k:'remarks',lbl:'Remarks',grow:true}].map(f=>(
                <div className={`pi2-rdfield${f.grow?' grow':''}`} key={f.k}>
                  <label>{f.lbl}</label>
                  <input value={insVals[f.k]||''} disabled={!selRow} onChange={e=>handleInsChange(f.k,e.target.value)}/>
                </div>
              ))}
            </div>
          </div>

          {/* Bill / Tax / Amount */}
          <div className="pi2-billrow">
            <div className="pi2-card">
              <div className="pi2-cardhead"><span className="pi2-dot"/>Bill Details
                <button type="button" className="pi2-addfld"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-billrows">
                {[['discPct','Disc Perc%',true],['freight','Freight',false],['roundOff','Round Off',false]].map(([k,lbl,chk])=>(
                  <div className="pi2-brow" key={k}>
                    <label>{chk?<><input type="checkbox" defaultChecked/>{lbl}</>:lbl}</label>
                    <input className="bval pi2-mono" value={bill[k]} onChange={e=>setBill(p=>({...p,[k]:e.target.value}))}/>
                    {k==='freight'&&<span className="pi2-gstinline">
                      <input type="checkbox" checked={bill.freightGst} onChange={e=>setBill(p=>({...p,freightGst:e.target.checked}))}/>GST
                    </span>}
                    {k==='roundOff'&&<button type="button" className="pi2-plusbtn">+</button>}
                  </div>
                ))}
                {bill.freightGst&&<div className="pi2-brow readout"><label>FreightGST</label><span className="bro-val">{fmt2(freightGST)}</span></div>}
              </div>
            </div>
            <div className="pi2-card">
              <div className="pi2-cardhead"><span className="pi2-dot"/>Tax Breakup
                <button type="button" className="pi2-addfld"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-amtlist">
                {[['CGST Amt',fmt2(cgstAmt)],['SGST Amt',fmt2(sgstAmt)],['IGST Amt','0.00'],['UTGST Amt','0.00']].map(([l,v])=>(
                  <div className="r" key={l}><span>{l}</span><span className="pi2-mono">{v}</span></div>
                ))}
              </div>
            </div>
            <div className="pi2-card pi2-amtcard">
              <div className="pi2-cardhead"><span className="pi2-dot"/>Amount Details
                <button type="button" className="pi2-addfld"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-amtlist">
                <div className="r"><span>Gross Amount</span><span className="pi2-mono">{fmtIn(grossAmt)}</span></div>
                <div className="r"><span>Discount</span><span className="pi2-mono">{fmtIn(discAmt)}</span></div>
                <div className="r"><span>GST Amount</span><span className="pi2-mono">{fmtIn(totGST)}</span></div>
                <div className="r total"><span>Net Amount</span><span className="pi2-mono">{fmtIn(netAmt)}</span></div>
              </div>
            </div>
          </div>
        </div>{/* /leftcol */}

        {/* Right rail */}
        <div className="pi2-rail">
          <div className="pi2-railtop">
            <button type="button" className="pi2-btn primary" onClick={()=>setShowNewItem(true)} data-testid="grn-new-item-btn">
              ＋ New Item <span className="pi2-kbd">F2 / F3</span>
            </button>
            <div className="pi2-import-wrap">
              <button type="button" className="pi2-btn" onClick={()=>setImportOpen(o=>!o)}>
                ⬆ Import <span style={{fontSize:9,marginLeft:2}}>▾</span>
              </button>
              <div className={`pi2-import-dd${importOpen?' open':''}`}>
                {['📊 Excel (.xlsx)','📄 CSV','📕 PDF','🖨 Scan','📟 PDA'].map(x=>(
                  <button key={x} type="button" onClick={()=>{setImportOpen(false);showToastMsg(`Import ${x} — coming soon`);}}>{x}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Inspector */}
          <div className="pi2-inspector" data-testid="grn-inspector-panel">
            {!selRow?(
              <div className="pi2-insp-empty">
                <div className="ico">🔍</div>
                <b>No row selected</b>
                <span>Click any item in the grid to view its full classification, pricing &amp; tax properties here.</span>
              </div>
            ):(
              <>
                <div className="pi2-insp-head">
                  <div className="eyebrow">Item Properties · Selected Row</div>
                  <div className="pi2-headgrid">
                    <div className="pi2-hg-cell"><span className="pi2-hg-k">Code</span><span className="pi2-hg-v">{insVals.code||'—'}</span></div>
                    <div className="pi2-hg-cell"><span className="pi2-hg-k">Article</span><span className="pi2-hg-v">{insVals.artCode||'—'}</span></div>
                    <div className="pi2-hg-cell"><span className="pi2-hg-k">HSN</span><span className="pi2-hg-v">{insVals.hsn||'—'}</span></div>
                    <div className="pi2-hg-cell"><span className="pi2-hg-k">Name</span><span className="pi2-hg-v">{insVals.name||'—'}</span></div>
                  </div>
                </div>
                <div className="pi2-insp-tabs">
                  {[['cls','Classification'],['num','Numeric / Pricing'],['img','Image'],['info','Additional Info']].map(([t,l])=>(
                    <button key={t} className={`pi2-insp-tab${insTab===t?' active':''}`} onClick={()=>setInsTab(t)}>{l}</button>
                  ))}
                </div>
                <div className="pi2-insp-body">
                  {insTab==='cls'&&(
                    <div className="pi2-insect">
                      <div className="pi2-sectitle">Classification Attributes
                        <button type="button" className="pi2-addfld" style={{fontSize:9.5}}><span>+</span>Add Field</button>
                      </div>
                      {clsFields.map(f=>(
                        <div className="pi2-edrow" key={f.k}>
                          <label>{f.lbl}</label>
                          {CLS_LKP[f.k]
                            ?<div className="pi2-autoinput">
                                <AutoInput value={insVals[f.k]||''} dataKey={f.k.charAt(0).toUpperCase()+f.k.slice(1)}
                                  onChange={v=>handleInsChange(f.k,v)}/>
                                <button type="button" className="pi2-lkpbtn"
                                  onClick={()=>openLookup(parseSI(CLS_LKP[f.k]),`Select ${f.lbl}`,(d)=>handleInsChange(f.k,d))}>🔍</button>
                              </div>
                            :<input value={insVals[f.k]||''} onChange={e=>handleInsChange(f.k,e.target.value)}/>
                          }
                        </div>
                      ))}
                    </div>
                  )}
                  {insTab==='num'&&(
                    <div className="pi2-insect">
                      <div className="pi2-sectitle">Numeric / Pricing</div>
                      {[['qty','Qty'],['rate','Rate'],['mrp','MRP'],['wsp','WSP'],['disc','Disc Amt'],
                        ['cgst','CGST %'],['sgst','SGST %'],['inputTax','Input Tax %'],['outputTax','Output Tax %']].map(([k,lbl])=>(
                        <div className="pi2-edrow" key={k}>
                          <label>{lbl}</label>
                          <input className="mono" value={insVals[k]||''} onChange={e=>handleInsChange(k,e.target.value)}/>
                        </div>
                      ))}
                    </div>
                  )}
                  {insTab==='img'&&(
                    <div style={{padding:12,display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
                      {/* Show existing thumbnail from localStorage */}
                      {selRow?.code&&loadImages(selRow.code).length>0?(
                        <div style={{display:'flex',flexWrap:'wrap',gap:6,justifyContent:'center'}}>
                          {loadImages(selRow.code).map((img,i)=>(
                            <img key={i} src={img.small} alt="" style={{width:80,height:80,objectFit:'cover',borderRadius:4,border:'1px solid var(--pi2-border)'}}/>
                          ))}
                        </div>
                      ):(
                        <div style={{fontSize:28,opacity:.4}}>📷</div>
                      )}
                      <button type="button" className="pi2-addfld" style={{margin:'0 auto'}}
                        onClick={()=>setImgModal({row:selRow,idx:selIdx})}>
                        🖼 Upload / Crop / Attach Link
                      </button>
                      <div style={{fontSize:11,color:'var(--pi2-ink-soft)',textAlign:'center'}}>
                        Full-resolution preview for the selected row.<br/>Changes here also update the grid thumbnail.
                      </div>
                    </div>
                  )}
                  {insTab==='info'&&(
                    <>
                      <div className="pi2-insect">
                        <div className="pi2-sectitle">Additional Info
                          <button type="button" className="pi2-addfld" style={{fontSize:9.5}}><span>+</span>Add Field</button>
                        </div>
                        {[['remarks','Remarks'],['payDueDate','Payment Due Date'],['payDisc','Payment Discount %'],['cashDisc','Cash Discount %']].map(([k,lbl])=>(
                          <div className="pi2-edrow" key={k}><label>{lbl}</label>
                            <input value={insVals[k]||''} onChange={e=>handleInsChange(k,e.target.value)}/>
                          </div>
                        ))}
                        <div className="pi2-edrow-footer">
                          <button className="pi2-btn" type="button" style={{fontSize:11}}>📜 Supplier History</button>
                          <button className="pi2-btn" type="button" style={{fontSize:11,marginLeft:4}}>📋 Pending Bills</button>
                          <button className="pi2-btn" type="button" style={{fontSize:11,marginLeft:4}}>🤖 Purchase Analysis (AI)</button>
                        </div>
                      </div>
                      <div className="pi2-insect">
                        <div className="pi2-sectitle">Against P.O
                          <button type="button" className="pi2-addfld" style={{fontSize:9.5}}><span>+</span>Field</button>
                        </div>
                        {[['poNo','P.O No'],['ordQty','Ordered Qty'],['pendQty','Pending Qty']].map(([k,lbl])=>(
                          <div className="pi2-edrow" key={k}><label>{lbl}</label>
                            <input value={insVals[k]||'—'} disabled/>
                          </div>
                        ))}
                        <div style={{padding:'6px 8px'}}>
                          <button className="pi2-btn" type="button" style={{fontSize:10}} onClick={()=>setSelIdx(null)}>
                            Clear Selection <span style={{fontSize:9}}>Esc</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Rail actions */}
          <div className="pi2-rail-actions">
            <button type="button" className="pi2-btn primary" onClick={handleSave} data-testid="grn-save-btn">
              Save <span className="pi2-kbd">F8</span>
            </button>
            <button type="button" className="pi2-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="pi2-btn" onClick={onBack} data-testid="grn-back-btn">
              ← Back to List
            </button>
          </div>
        </div>
      </div>{/* /body */}

      {/* Status bar */}
      <div className="pi2-statusbar">
        <span>Ready…</span>
        <span>Branch: <b>Main Branch</b></span>
        <span>User: <b>SSADMIN</b></span>
        <span>Business day: <b>29-Aug-2026</b></span>
        <span>Screen Id: <b>{cfg.sid}</b></span>
        <span>Time: <b>{clock}</b></span>
      </div>

      {/* Toast */}
      <div className={`pi2-toast${toastVis?'':' hidden'}`}>{toast}</div>

      {/* Modals */}
      {showNewItem&&(
        <NewItemModal
          initialValues={editRow}
          onSave={row=>{
            if(editRow&&selIdx!==null){
              setRows(p=>p.map((r,i)=>i===selIdx?{...r,...row,_id:r._id}:r));
              showToastMsg('Row updated');
            } else {
              setRows(p=>[...p,row]);
              showToastMsg('Item added to grid');
            }
          }}
          onClose={()=>{setShowNewItem(false);setEditRow(null);}}/>
      )}
      {showSizeCfg&&(
        <SizeConfigModal
          selRow={selRow}
          onSaveAll={newRows=>{setRows(p=>[...p,...newRows]);showToastMsg(`${newRows.length} size rows added`);}}
          onClose={()=>setShowSizeCfg(false)}/>
      )}
      {popup&&!showNewItem&&!showSizeCfg&&(
        <GetInfoPopup
          screenId={popup.si.screenId}
          controlId={String(popup.si.controlId)}
          title={popup.title}
          onSelect={handleLkpSelect}
          onClose={()=>setPopup(null)}/>
      )}
      {/* Row context menu modals */}
      {histModal?.type==='item'&&<ItemHistoryModal row={histModal.row} onClose={()=>setHistModal(null)}/>}
      {histModal?.type==='supplier'&&<SupplierHistoryModal row={histModal.row} onClose={()=>setHistModal(null)}/>}
      {imgModal&&<ImageManagerModal
        itemKey={imgModal.row?.code||`row_${imgModal.row?._id}`}
        itemLabel={imgModal.row?.name||'Item'}
        onSave={(ref)=>{ updRow(imgModal.idx,'_imgUrl',ref.firstThumb); updRow(imgModal.idx,'_imgRef',ref.imageKey); }}
        onClose={()=>setImgModal(null)}/>}
      {imeiModal2&&<UnitIdentifierModal row={imeiModal2}
        onSave={data=>showToastMsg(`Unit data saved for ${data.units?.length} unit(s)`)}
        onClose={()=>setImeiModal2(null)}/>}
    </div>
  );
}

// ─── Main export: list → form ──────────────────────────────────────────────
export default function GRNScreen({ screenId, theme, navigateTo }) {
  const [view,   setView]   = useState('list');
  const [editId, setEditId] = useState(null);

  if (view === 'form') {
    return <GRNForm screenId={screenId} editId={editId} onBack={()=>{setView('list');setEditId(null);}}/>;
  }
  return (
    <GRNList
      screenId={screenId}
      onNewEntry={()=>setView('form')}
      onEditEntry={r=>{setEditId(r.id||null);setView('form');}}
      onBack={navigateTo ? ()=>navigateTo('HUB') : undefined}
    />
  );
}
